import { convertBreak } from "@lib/text"
import * as m from "@paraglide/messages"
import type { SupportedLocale as Lang } from "@site-types/content"
import type { ContactFormData } from "@site-types/send-mail"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { ATTACHMENT_SIZE_LIMIT } from "./constants"
import { resolveContactEndpoint } from "./contact-endpoint"
import { FormFields } from "./FormFields"
import { InquirySelection } from "./InquirySelection"
import { SendMailLangContext, useSendMailLang } from "./lang-context"

type ContactProps = {
  selectedType: string
  selectedPosition?: string
  careerList: string[]
}

export function buildContactDefaultFormValues({
  selectedType,
  selectedPosition,
  careerList,
}: ContactProps): ContactFormData {
  return {
    type: selectedType,
    position: selectedPosition || (careerList.length > 0 ? careerList[0] : ""),
    name: "",
    email: "",
    companyname: "",
    phonenumber: "",
    message: "",
    attachment: "",
    purpose: "",
    formSponsorshipStartDate: "",
    nickname: "", // honeypot field
    attachments: [],
  }
}

export function shouldIgnoreContactSubmission(current: Partial<Pick<ContactFormData, "nickname">>) {
  return Boolean(current.nickname && current.nickname.trim() !== "")
}

export function buildContactMailPayload(current: ContactFormData, lang: Lang) {
  const isRecruitment =
    current.type === m.contact_formType_options_recruitment({}, { locale: lang }) && current.position !== ""

  return {
    ...current,
    nickname: current.nickname,
    position:
      current.type === m.contact_formType_options_recruitment({}, { locale: lang }) ? current.position : "",
    mailContent: isRecruitment
      ? m.contact_formContent_thanksRecruitment({}, { locale: lang })
      : m.contact_formContent_thanksGeneral({}, { locale: lang }),
    mailLanguage: lang,
    mailHeading: {
      type: m.contact_formType_label({}, { locale: lang }),
      position: m.contact_formPosition_label({}, { locale: lang }),
      name: m.contact_formName_label({}, { locale: lang }),
      companyName: m.contact_formCompanyName_label({}, { locale: lang }),
      email: m.contact_formEmail_label({}, { locale: lang }),
      phoneNumber: m.contact_formPhoneNumber_label({}, { locale: lang }),
      message: m.contact_formMessage_label({}, { locale: lang }),
      purpose: m.contact_formPurpose_label({}, { locale: lang }),
      formSponsorshipStartDate: m.contact_formPurpose_startDate_title({}, { locale: lang }),
    },
    mailSubjectToPB: isRecruitment
      ? lang === "ja"
        ? `${current.position}${m.contact_formContent_recruitment_subjectToPB({}, { locale: lang })}`
        : `${m.contact_formContent_recruitment_subjectToPB({}, { locale: lang })} ${current.position}`
      : lang === "ja"
        ? `${m.contact_formContent_general_subjectToPB({}, { locale: lang })}${current.type ? `【${current.type}】` : ""}`
        : `${m.contact_formContent_general_subjectToPB({}, { locale: lang })} ${current.type.toLowerCase()}`,
    mailSubjectToUser: isRecruitment
      ? lang === "ja"
        ? `${current.position}${m.contact_formContent_recruitment_mailSubjectToUser({}, { locale: lang })}`
        : `${m.contact_formContent_recruitment_mailSubjectToUser({}, { locale: lang })} ${current.position}`
      : lang === "ja"
        ? `${m.contact_formContent_general_mailSubjectToUser({}, { locale: lang })}`
        : `${m.contact_formContent_general_mailSubjectToUser({}, { locale: lang })}`,
    message: current.message && current.message !== "" ? convertBreak(current.message) : "",
    purpose: current.purpose && current.purpose !== "" ? convertBreak(current.purpose) : "",
    formSponsorshipStartDate: current.formSponsorshipStartDate,
    attachments: current.attachments,
  }
}

const Contact = ({ selectedType, selectedPosition, careerList }: ContactProps) => {
  const navigate = useNavigate()
  const lang = useSendMailLang()
  const FORM_DATA_DEFAULT_STATE = buildContactDefaultFormValues({
    selectedType,
    selectedPosition,
    careerList,
  })

  const {
    watch,
    setValue,
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ContactFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: FORM_DATA_DEFAULT_STATE,
  })

  const [loading, setLoading] = useState(false)
  const [sendError, setSendError] = useState(false)
  const [sent, setSent] = useState(false)
  const timeoutIdsRef = useRef<Array<ReturnType<typeof setTimeout>>>([])

  const formData = watch()

  useEffect(() => {
    return () => {
      timeoutIdsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
      timeoutIdsRef.current = []
    }
  }, [])

  const scheduleContactDelay = useCallback((callback: () => void, delayMs: number) => {
    const timeoutId = setTimeout(() => {
      timeoutIdsRef.current = timeoutIdsRef.current.filter((current) => current !== timeoutId)
      callback()
    }, delayMs)
    timeoutIdsRef.current.push(timeoutId)
  }, [])

  useEffect(() => {
    register("attachments", {
      validate: (files) => {
        const list = Array.isArray(files) ? files : []
        if (list.length > 2) return m.contact_formAttachment_quantityValidation({}, { locale: lang })
        const tooLarge = list.some((f) => f.size > ATTACHMENT_SIZE_LIMIT)
        if (tooLarge) return m.contact_formAttachment_validation({}, { locale: lang })
        return true
      },
    })

    const isRecruitment = formData.type === m.contact_formType_options_recruitment({}, { locale: lang })
    const isSponsorship = formData.type === m.contact_formType_options_sponsorship({}, { locale: lang })

    register("position", {
      validate: (v) =>
        isRecruitment ? (v && v.trim() !== "") || m.contact_formName_validation({}, { locale: lang }) : true,
    })
    register("formSponsorshipStartDate", {
      validate: (v) =>
        isSponsorship ? (v && v.trim() !== "") || m.contact_formName_validation({}, { locale: lang }) : true,
    })
  }, [register, lang, formData.type])

  const sendEmail = async (current: ContactFormData) => {
    // Honeypot guard: RHF should block, but double-guard
    if (shouldIgnoreContactSubmission(current)) return

    setLoading(true)

    const formDataToSend = buildContactMailPayload(current, lang)

    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formDataToSend),
    }

    try {
      const response = await fetch(resolveContactEndpoint(), options)
      if (response.ok) {
        setSent(true)
        scheduleContactDelay(() => {
          setLoading(false)
          void navigate({
            params: { locale: lang },
            to: "/$locale/contact/thanks",
          })
        }, 1000)
      } else {
        setSent(false)
        setSendError(true)
        scheduleContactDelay(() => setLoading(false), 3000)
      }
    } catch (error) {
      setSent(false)
      setSendError(true)
      scheduleContactDelay(() => setLoading(false), 1000)
      console.error("Error:", error)
    }
  }

  return (
    <SendMailLangContext.Provider value={lang}>
      <form
        onSubmit={handleSubmit(sendEmail)}
        noValidate
        autoComplete="off"
        data-testid="contact-form"
        data-id="contact-form"
      >
        {/* Honeypot field (invisible to users, visible to simple bots) */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-10000px",
            top: "auto",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        >
          <label htmlFor="nickname">Do not fill this field</label>
          <input
            type="text"
            id="nickname"
            {...register("nickname", {
              validate: (v) => !v || v.trim() === "" || "bot",
            })}
            autoComplete="off"
            tabIndex={-1}
            inputMode="text"
          />
        </div>
        <InquirySelection
          setValue={setValue}
          value={formData.type}
        />

        {formData.type === m.contact_formType_options_generalEnquiry({}, { locale: lang }) && (
          <FormFields
            register={register}
            errors={errors}
            setValue={setValue}
            values={formData}
            showInputs={{
              formPosition: false,
              formName: true,
              formCompanyName: true,
              formEmail: true,
              formPhoneNumber: true,
              formSponsorshipStartDate: false,
              formPurpose: false,
              formMessage: true,
              formAttachments: true,
            }}
            messageRules={{
              validate: (v) =>
                (typeof v === "string" && v.trim() !== "") ||
                m.contact_formMessage_validation({}, { locale: lang }),
            }}
            purposeRules={{}}
          />
        )}

        {formData.type === m.contact_formType_options_recruitment({}, { locale: lang }) && (
          <FormFields
            register={register}
            errors={errors}
            setValue={setValue}
            values={formData}
            showInputs={{
              formPosition: true,
              formName: true,
              formCompanyName: true,
              formEmail: true,
              formPhoneNumber: true,
              formSponsorshipStartDate: false,
              formPurpose: false,
              formMessage: true,
              formAttachments: true,
            }}
            careerList={careerList}
            messageRules={{
              validate: (v) =>
                (typeof v === "string" && v.trim() !== "") ||
                m.contact_formMessage_validation({}, { locale: lang }),
            }}
            purposeRules={{}}
          />
        )}

        {formData.type === m.contact_formType_options_salesProposals({}, { locale: lang }) && (
          <FormFields
            register={register}
            errors={errors}
            setValue={setValue}
            values={formData}
            showInputs={{
              formPosition: false,
              formName: true,
              formCompanyName: true,
              formEmail: true,
              formPhoneNumber: true,
              formSponsorshipStartDate: false,
              formPurpose: false,
              formMessage: true,
              formAttachments: true,
            }}
            messageRules={{
              validate: (v) =>
                (typeof v === "string" && v.trim() !== "") ||
                m.contact_formMessage_validation({}, { locale: lang }),
            }}
            purposeRules={{}}
          />
        )}

        {formData.type === m.contact_formType_options_mediaEnquiry({}, { locale: lang }) && (
          <FormFields
            register={register}
            errors={errors}
            setValue={setValue}
            values={formData}
            showInputs={{
              formPosition: false,
              formName: true,
              formCompanyName: true,
              formEmail: true,
              formPhoneNumber: true,
              formSponsorshipStartDate: false,
              formPurpose: false,
              formMessage: true,
              formAttachments: true,
            }}
            messageRules={{
              validate: (v) =>
                (typeof v === "string" && v.trim() !== "") ||
                m.contact_formMessage_validation({}, { locale: lang }),
            }}
            purposeRules={{}}
          />
        )}

        {formData.type === m.contact_formType_options_sponsorship({}, { locale: lang }) && (
          <FormFields
            register={register}
            errors={errors}
            setValue={setValue}
            values={formData}
            showInputs={{
              formPosition: false,
              formName: true,
              formCompanyName: true,
              formEmail: true,
              formPhoneNumber: true,
              formSponsorshipStartDate: true,
              formPurpose: true,
              formMessage: false,
              formAttachments: true,
            }}
            messageRules={{}}
            purposeRules={{
              validate: (v) =>
                (typeof v === "string" && v.trim() !== "") ||
                m.contact_formMessage_validation({}, { locale: lang }),
            }}
          />
        )}

        <button
          data-id="contact-submit"
          className="mx-auto flex cursor-pointer border-0 bg-[var(--blue)] px-10 py-6 text-[2rem] text-[var(--white)] transition duration-300 ease-[ease] [--clip-inset:10px] [clip-path:polygon(0%_0%,calc(100%_-_var(--clip-inset))_0%,100%_var(--clip-inset),100%_100%,var(--clip-inset)_100%,0%_calc(100%_-_var(--clip-inset)))] hover:bg-[var(--pink)] hover:no-underline disabled:pointer-events-none disabled:opacity-50"
          type="submit"
          disabled={!isValid || isSubmitting}
        >
          {m.contact_submitButton_label({}, { locale: lang })}
        </button>

        {loading && (
          <div
            data-id="contact-loading"
            className="fixed top-0 left-0 flex h-full w-full justify-center bg-[var(--black-50)] pt-[20%] text-center"
          >
            <div className="h-fit bg-[var(--deep-blue)] px-8 py-6 text-[var(--white)]">
              <p>{m.contact_sending({}, { locale: lang })}</p>
              {!sent ? (
                <p data-id="contact-loading-spinner">
                  <i className="fa-solid fa-spinner [animation:loading_1s_linear_infinite]"></i>
                </p>
              ) : (
                <p data-id="contact-loading-done">{m.contact_sending_done({}, { locale: lang })}</p>
              )}
              {sendError && (
                <p data-id="contact-loading-error">{m.contact_sending_error({}, { locale: lang })}</p>
              )}
            </div>
          </div>
        )}
      </form>
    </SendMailLangContext.Provider>
  )
}

export default Contact
